import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { memo } from 'react';
import { Icon } from 'components';
import { Row } from 'components/layout';
import { USERINFO_LABELS } from 'hooks/useCandidateProfile';

import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';

import styles from '../../ProfileSection.common.module.scss';

interface OptionalProfileSectionProps {
  onLabelClick: (label: USERINFO_LABELS) => void;
}

export const OptionalProfileSection: React.FC<OptionalProfileSectionProps> =
  memo(({ onLabelClick }: OptionalProfileSectionProps) => {
    const {
      extraCurricularComplete,
      skillsComplete,
      demographiclInfoComplete,
    } = useCandidateProfile();

    const [t] = useTranslation('candidate.profile');
    return (
      <section className={styles['optional-container']}>
        <p className={styles.sectionTitle}>{t('sidebar.main.optional')}</p>
        <button
          onClick={() =>
            onLabelClick(USERINFO_LABELS.EXTRA_CURRICULAR_ACTIVITIES)
          }
          className={styles.sectionButton}
        >
          <Row
            align="center"
            justify="space-between"
            className={styles.sectionRow}
          >
            <Row
              align="center"
              className={cn(styles.sectionLabel, {
                [styles.sectionLabelComplete]: extraCurricularComplete,
              })}
            >
              <Icon
                iconName="icon_crown"
                className={cn(styles.iconIncomplete, {
                  [styles.iconComplete]: extraCurricularComplete,
                })}
              />
              <p>{t('sidebar.main.extra-curricular')}</p>
            </Row>
            {extraCurricularComplete ? (
              <Icon
                key={1}
                iconName="tick-mark-circle"
                className={styles.tickCircleComplete}
              />
            ) : (
              <Icon
                key={2}
                iconName="icon_check"
                className={styles.tickCircleIncomplete}
              />
            )}
          </Row>
        </button>
        <button
          onClick={() => onLabelClick(USERINFO_LABELS.SKILLS)}
          className={styles.sectionButton}
        >
          <Row
            align="center"
            justify="space-between"
            className={styles.sectionRow}
          >
            <Row
              align="center"
              className={cn(styles.sectionLabel, {
                [styles.sectionLabelComplete]: skillsComplete,
              })}
            >
              <Icon
                iconName="icon_bicep"
                className={cn(styles.iconIncomplete, {
                  [styles.iconComplete]: skillsComplete,
                })}
              />
              <p>{t('sidebar.main.skills')}</p>
            </Row>
            {skillsComplete ? (
              <Icon
                key={1}
                iconName="tick-mark-circle"
                className={styles.tickCircleComplete}
              />
            ) : (
              <Icon
                key={2}
                iconName="icon_check"
                className={styles.tickCircleIncomplete}
              />
            )}
          </Row>
        </button>
        <button
          onClick={() => onLabelClick(USERINFO_LABELS.DEMOGRAPHIC_INFO)}
          className={styles.sectionButton}
        >
          <Row
            align="center"
            justify="space-between"
            className={styles.sectionRow}
          >
            <Row
              align="center"
              className={cn(styles.sectionLabel, {
                [styles.sectionLabelComplete]: demographiclInfoComplete,
              })}
            >
              <Icon
                iconName="icon_info"
                className={cn(styles.iconIncomplete, {
                  [styles.iconComplete]: demographiclInfoComplete,
                })}
              />
              <p>{t('sidebar.main.demographic')}</p>
            </Row>
            {demographiclInfoComplete ? (
              <Icon
                key={1}
                iconName="tick-mark-circle"
                className={styles.tickCircleComplete}
              />
            ) : (
              <Icon
                key={2}
                iconName="icon_check"
                className={styles.tickCircleIncomplete}
              />
            )}
          </Row>
        </button>
      </section>
    );
  });

OptionalProfileSection.displayName = 'OptionalProfileSection';
